'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import type { Provider } from '@supabase/supabase-js';
import { UserCheck } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@kit/ui/alert';
import { Button } from '@kit/ui/button';
import { If } from '@kit/ui/if';
import { Trans } from '@kit/ui/trans';

import { useEmailAuthHint } from '../hooks/use-email-auth-hint';
import { OauthProviders } from './oauth-providers';

/**
 * @name AuthRecoveryHint
 * @description
 * Turns an account-related auth failure into a DIRECTED next step instead of a
 * dead-end message. Given the email that just failed, it asks the server which
 * methods the account actually uses and renders the right action:
 *
 *   - OAuth-only account (e.g. Google) → a one-click "Continue with Google"
 *     button (reuses `OauthProviders`, so the request matches the normal path).
 *   - Otherwise, on sign-up → a "Sign in" button (the account already exists).
 *
 * It never throws and renders nothing until it has a useful, existence-backed
 * hint, so it can be mounted unconditionally next to an error alert.
 *
 * Mount this ONLY when the relevant error is present (see `auth-error-matchers`)
 * so the lookup fires only on real account collisions.
 */
export function AuthRecoveryHint({
  email,
  context,
  signInPath = '/auth/sign-in',
  oauthPaths,
}: {
  /** The email that just failed to sign in / sign up. */
  email: string;
  /** Which screen we're on — decides the fallback action. */
  context: 'sign-in' | 'sign-up';
  /** Where the "Sign in" fallback link points. */
  signInPath?: string;
  /**
   * Callback + return paths for the one-click provider button. When omitted,
   * the provider button is not offered and we fall back to the sign-in link.
   */
  oauthPaths?: { callback: string; returnPath: string };
}) {
  const hint = useEmailAuthHint();
  const { mutate } = hint;

  useEffect(() => {
    const trimmed = email.trim();

    if (trimmed) {
      mutate(trimmed);
    }
  }, [email, mutate]);

  const data = hint.data;

  // Nothing helpful to add: unknown email, or the lookup hasn't resolved.
  if (!data || !data.exists) {
    return null;
  }

  const oauthProviders = data.providers.filter(
    (provider) => provider !== 'email' && provider !== 'phone',
  );
  const primaryProvider = oauthProviders[0];
  const isOauthOnly = Boolean(primaryProvider) && !data.hasPassword;
  const canOfferProvider = isOauthOnly && Boolean(oauthPaths);

  // On the sign-in screen a wrong password (account HAS a password) is already
  // served by the inline error + "forgot password" link — only step in when we
  // can redirect them to a different, correct method (their OAuth provider).
  if (context === 'sign-in' && !canOfferProvider) {
    return null;
  }

  const signInHref = `${signInPath}?email=${encodeURIComponent(email.trim())}`;

  return (
    <Alert data-test={'auth-recovery-hint'}>
      <UserCheck className={'h-4 w-4'} />

      <AlertTitle>
        <Trans i18nKey={'auth:recovery.heading'} />
      </AlertTitle>

      <AlertDescription className={'flex flex-col gap-3'}>
        <span>
          <If
            condition={isOauthOnly && Boolean(primaryProvider)}
            fallback={<Trans i18nKey={'auth:recovery.alreadyHaveAccount'} />}
          >
            <Trans
              i18nKey={'auth:recovery.useProvider'}
              values={{
                provider: primaryProvider ? providerLabel(primaryProvider) : '',
              }}
            />
          </If>
        </span>

        <If
          condition={canOfferProvider && Boolean(primaryProvider)}
          fallback={
            <Button asChild className={'w-full'}>
              <Link href={signInHref} prefetch={true}>
                <Trans i18nKey={'auth:recovery.signInAction'} />
              </Link>
            </Button>
          }
        >
          <OauthProviders
            enabledProviders={[primaryProvider as Provider]}
            shouldCreateUser={false}
            paths={oauthPaths!}
          />
        </If>
      </AlertDescription>
    </Alert>
  );
}

/**
 * Human label for a provider id. Mirrors `getProviderName` in
 * `oauth-providers.tsx` (kept local to avoid exporting an internal helper).
 */
function providerLabel(providerId: string): string {
  const capitalize = (value: string) =>
    value.slice(0, 1).toUpperCase() + value.slice(1);

  if (providerId.endsWith('.com')) {
    return capitalize(providerId.split('.com')[0]!);
  }

  return capitalize(providerId);
}
